// Sound page in the redesign (resources/views/livewire/sounds/sound-page-v2.blade.php).
// soundPage: Track / Save with undo, hide from Trending sounds (the ranked table's list, kept in this browser), copy
// link and ids, CSV export of the daily videos, and the section tabs. spGrowth: the interactive growth chart, drawn
// at the box's real width so its text stays crisp; hover, touch or the arrow keys read any day.

import { drawTotal, nearestReading, sinceLast } from './growth-line';
import { term } from '../terms';

const HIDDEN_KEY = 'rt-hidden'; // the ranked table's hidden rows (resources/js/alpine/ranked-table.js)

const read = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch (e) { return fallback; } };
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {} };
const toast = (detail) => window.dispatchEvent(new CustomEvent('ui-toast', { detail }));

export function soundPage(cfg = {}) {
    return {
        saved: !!cfg.saved,
        tracked: !!cfg.tracked,
        current: null,

        init() {
            const onScroll = () => {
                let id = null;
                document.querySelectorAll('.sp [id^="sx-"]').forEach((el) => {
                    if (el.getBoundingClientRect().top < 160) id = el.id;
                });
                this.current = id;
            };
            let raf = null;
            window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = null; onScroll(); }); }, { passive: true });
        },

        go(id) {
            const el = document.getElementById(id);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            this.current = id;
            history.replaceState(null, '', `#${id}`);
        },

        async favourite() {
            const was = this.saved;
            this.saved = !was;
            try {
                const now = await this.$wire.toggleFavourite();
                this.saved = now;
                toast({ tone: 'ok', title: now ? 'Saved' : 'Removed from Saved', text: cfg.title, link: now ? { label: 'View saved', to: 'favourites' } : null, action: { label: 'Undo', run: () => this.favourite() } });
            } catch (e) {
                this.saved = was;
                toast({ tone: 'error', title: "Couldn't save that", text: 'Nothing changed. Try again in a moment.' });
            }
        },

        async track() {
            const was = this.tracked;
            this.tracked = !was;
            try {
                const now = await this.$wire.toggleTracked();
                this.tracked = now;
                toast({ tone: 'ok', title: now ? `Tracking this ${term('sound')}` : 'Stopped tracking', text: now ? `${cfg.title} is in your tracked ${term('sounds')}` : cfg.title, link: now ? { label: `View tracked ${term('sounds')}`, to: 'trackedSounds' } : null, action: { label: 'Undo', run: () => this.track() } });
            } catch (e) {
                this.tracked = was;
                toast({ tone: 'error', title: "Couldn't change tracking", text: 'Nothing changed. Try again in a moment.' });
            }
        },

        hide() {
            write(HIDDEN_KEY, { ...read(HIDDEN_KEY, {}), [cfg.id]: true });
            toast({ tone: 'info', title: `Hidden from Trending ${term('sounds')}`, text: `${cfg.title} won't show in your Trending ${term('sounds')} list on this browser.`, action: { label: 'Undo', run: () => {
                const { [cfg.id]: _, ...rest } = read(HIDDEN_KEY, {});
                write(HIDDEN_KEY, rest);
            } } });
        },

        async copy(text, label = 'Link') {
            try {
                await navigator.clipboard.writeText(text);
                toast({ tone: 'ok', title: `${label} copied`, text });
            } catch (e) {
                toast({ tone: 'error', title: `Couldn't copy the ${label.toLowerCase()}`, text: 'Your browser blocked the clipboard.' });
            }
        },

        copyLink() { return this.copy(new URL(cfg.url, window.location.origin).href, 'Link'); },

        exportCsv() {
            const box = document.querySelector('.sp-chartcard');
            const points = box ? window.Alpine.$data(box).points : [];
            const rows = [['date', 'total_videos', 'new_videos', 'tokchart_score', 'worldwide_chart_position'], ...points.map((p) => [p.d, p.total ?? '', p.new ?? '', p.score ?? '', p.rank ?? ''])];
            const blob = new Blob([rows.map((r) => r.join(',')).join('\n')], { type: 'text/csv' });
            const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `tokchart-sound-${cfg.id}.csv` });
            a.click();
            URL.revokeObjectURL(a.href);
            toast({ tone: 'ok', title: 'Exported', text: `${points.length} days of videos for ${cfg.title}` });
        },
    };
}

const nf = (n) => (n == null ? '' : Math.round(n).toLocaleString('en-US'));
const short = (n) => {
    if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, '')}M`;
    if (n >= 1e3) return `${Math.round(n / 1e3)}K`;
    return `${Math.round(n)}`;
};
// Horizontal gridlines with their labels, leaving out a label that would sit on the one above it.
const gridlines = (values, py, w, fmt) => {
    const labelled = [];
    return values.map((v) => py(v)).map((y, k) => {
        const label = labelled.every((l) => Math.abs(y - l) >= 16) ? `<text x="4" y="${y - 4}" class="sp-g-t">${fmt(values[k])}</text>` : '';
        if (label) labelled.push(y);
        return `<line x1="0" x2="${w}" y1="${y}" y2="${y}" class="sp-g-gl"/>${label}`;
    }).join('');
};
const day = (d, year = false) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC', ...(year ? { year: 'numeric' } : {}) });

export function spGrowth(cfg = {}) {
    return {
        mode: 'total',
        days: String(cfg.days ?? (cfg.points.length > 30 ? 60 : 30)),
        all: cfg.points ?? [],
        svg: '',
        tip: null,
        at: null,
        geo: null,

        get points() { return this.all.slice(-Number(this.days)); },

        init() {
            this.draw();
            this.$watch('mode', () => this.draw());
            this.$watch('days', () => this.draw());
            const box = this.$refs.box ?? this.$el.querySelector('.sp-ichsvg');
            if (box && 'ResizeObserver' in window) new ResizeObserver(() => this.draw()).observe(box);
            // Palette switches change the colours through CSS; nothing to redraw.
        },

        draw() {
            const box = this.$refs.box ?? this.$el.querySelector('.sp-ichsvg');
            const w = Math.max(240, Math.round(box?.clientWidth || 700));
            const h = Math.max(150, Math.round(box?.clientHeight || 270));
            this.tip = null;
            const P = this.points;
            const n = P.length;
            const top = 14;
            const bot = h - 24;
            const bw = w / n;
            const px = (i) => i * bw + bw / 2;
            let body = '';
            let ys = [];

            if (this.mode === 'score') {
                // The score runs from 0 to 1,000; a fixed axis keeps one sound's chart comparable with another's.
                const py = (v) => bot - (v / 1000) * (bot - top);
                const first = P.findIndex((p) => p.score != null);
                if (first > 0) {
                    const x = px(first) - bw / 2;
                    body += `<rect x="0" y="${top}" width="${x.toFixed(1)}" height="${bot - top}" class="sp-g-off"/>`;
                    if (x > 140) body += `<text x="${(x / 2).toFixed(1)}" y="${(top + bot) / 2}" text-anchor="middle" class="sp-g-t">Not on the chart yet</text>`;
                }
                body += gridlines([250, 500, 750, 1000], py, w, nf);
                // Break the line on days the sound wasn't on the chart.
                let d = '';
                P.forEach((p, i) => { if (p.score == null) return; d += `${d && P[i - 1]?.score != null ? 'L' : 'M'}${px(i).toFixed(1)},${py(p.score).toFixed(1)}`; });
                body += `<path d="${d}" class="sp-g-line"/>`;
                const last = P.findLastIndex((p) => p.score != null);
                if (last >= 0) body += `<circle cx="${px(last).toFixed(1)}" cy="${py(P[last].score).toFixed(1)}" r="4.5" class="sp-g-dot"/>`;
                ys = P.map((p) => (p.score == null ? null : py(p.score)));
            } else {
                const known = P.filter((p) => p.total != null);
                const lo = Math.min(...known.map((p) => p.total));
                const hi = Math.max(...known.map((p) => p.total));
                // Start the axis near the first value when the sound was already big, so growth still shows.
                const base = lo > hi * 0.5 ? lo * 0.9 : 0;
                const mx = base + (hi - base) * 1.08 || 1;
                const py = (v) => bot - ((v - base) / (mx - base)) * (bot - top);
                // A big sound's axis can be only a few million tall: one decimal, so 37.7M and 36.5M don't read as 38M and 36M.
                const fine = hi >= 1e7 && (mx - base) / 4 < 1e7;
                body += gridlines([0.25, 0.5, 0.75].map((t) => mx - (mx - base) * t), py, w, (v) => (fine ? `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M` : short(v)));
                const line = drawTotal({ P, w, top, bot, px, py, milestones: cfg.milestones });
                body += line.body;
                ys = line.ys;
            }

            const ticks = [0, Math.floor(n / 3), Math.floor((n * 2) / 3), n - 1].filter((v, i, a) => a.indexOf(v) === i);
            body += ticks.map((i, j) => `<text x="${px(i).toFixed(1)}" y="${h - 6}" text-anchor="${j === 0 ? 'start' : j === ticks.length - 1 ? 'end' : 'middle'}" class="sp-g-t">${i === n - 1 && P[i].d === new Date().toISOString().slice(0, 10) ? 'Today' : day(P[i].d)}</text>`).join('');

            this.geo = { w, bw, ys };
            this.svg = `<svg viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" aria-hidden="true">${body}</svg>`;
            if (this.at !== null) this.show(Math.min(this.at, n - 1));
        },

        hover(e) {
            const r = this.$refs.box.getBoundingClientRect();
            const x = e.clientX - r.left;
            if (x < 0 || x > r.width || !this.geo) return;
            const i = Math.max(0, Math.min(this.points.length - 1, Math.floor(x / this.geo.bw)));
            this.show(this.mode === 'total' ? nearestReading(this.points, i) : i);
        },

        key(e) {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
            e.preventDefault();
            const n = this.points.length;
            const P = this.points;
            const step = e.key === 'ArrowLeft' ? -1 : 1;
            let i = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (this.at ?? n - 1) + step;
            // The line only has readings: Home/End go to the nearest one, the arrows to the next one along.
            if (this.mode === 'total') {
                if (e.key === 'Home' || e.key === 'End') i = nearestReading(P, i);
                else { while (i >= 0 && i < n && P[i].total == null) i += step; if (i < 0 || i >= n) i = this.at ?? nearestReading(P, n - 1); }
            }
            this.show(Math.max(0, Math.min(n - 1, i)));
        },

        leave() { this.tip = null; this.at = null; },

        show(i) {
            const P = this.points;
            const p = P[i];
            if (!p || !this.geo) return;
            this.at = i;
            // TikTok rounds big totals, so a reading can dip below the one before: never show that as lost videos.
            const since = sinceLast(P, i, { floor: true });
            const x = i * this.geo.bw + this.geo.bw / 2;
            const half = 105;
            this.tip = {
                x,
                y: this.geo.ys[i],
                left: Math.min(Math.max(x, half), this.geo.w - half),
                date: day(p.d, true),
                total: p.total != null ? nf(p.total) : 'No reading',
                since,
                score: p.score != null ? nf(p.score) : '',
                rank: p.rank ? `#${nf(p.rank)}` : '',
            };
        },
    };
}

// spBar: on phones the bottom bar (.sp-dock) holds the app's player while this page's sound plays. Docked (the default),
// the island (x-ui.player-dock) is hidden and the bar plays, pauses and seeks; its tab, tapped or dragged up, floats the
// island out again, and the island's dock button or a drag down puts it back. The choice is remembered in
// localStorage. Another sound playing keeps the island floating, with a close button.
const PLAYER_KEY = 'sp-player';     // 'float' once floated out; anything else is docked
const PHONE = '(max-width:760px)';
const getItem = (key) => { try { return localStorage.getItem(key); } catch (e) { return null; } };
const setItem = (key, value) => { try { localStorage.setItem(key, value); } catch (e) {} };
// Replays a one-shot body class (the island rising, the bar settling); CSS ignores it under reduced motion.
const flash = (cls) => { const b = document.body; b.classList.remove(cls); void b.offsetWidth; b.classList.add(cls); setTimeout(() => b.classList.remove(cls), 400); };

export function spBar(track) {
    let pull = null; // the tab's drag: start y and whether it moved
    return {
        floated: getItem(PLAYER_KEY) === 'float',

        get player() { return this.$store.uiPlayer; },
        get mine() { return this.player.isCurrent(track.src); },
        /** docked: this sound, in the bar; float: this sound, in the island; idle: nothing or another sound. */
        get state() { return this.mine ? (this.floated ? 'float' : 'docked') : 'idle'; },
        get docked() { return this.state === 'docked'; },
        get sub() {
            const p = this.player;
            if (!this.docked) return track.subtitle;
            if (p.error) return p.error;
            return p.duration ? `${p.clock(p.time)} / ${p.clock(p.duration)} · ${track.subtitle}` : track.subtitle;
        },
        get coverLabel() { return `${this.mine && !this.player.paused ? 'Pause' : 'Play'} ${track.title}`; },

        init() {
            this.player.dockKey = track.src;
            this.dragIsland();
        },

        /** x-effect: hide the island while docked. */
        sync() {
            const docked = this.docked;
            document.body.classList.toggle('sp-pdocked', docked);
            if (docked) this.player.expanded = false;
        },

        /** A press on the bar's cover while another sound plays: this sound takes over, in the bar. */
        claim() { if (this.player.open && !this.mine) this.remember(false); },

        remember(floated) { this.floated = floated; setItem(PLAYER_KEY, floated ? 'float' : 'dock'); },

        floatOut() {
            if (!this.docked) return;
            const fromKeys = document.activeElement === this.$refs.pull;
            this.remember(true);
            flash('sp-prise');
            if (fromKeys) this.$nextTick(() => document.querySelector('.ui-isl-x button:not([style*="none"])')?.focus());
        },

        dock() {
            if (!this.mine || !this.floated) return;
            const fromKeys = !!document.activeElement?.closest('.ui-dock');
            this.remember(false);
            flash('sp-psettle');
            if (fromKeys) this.$nextTick(() => this.$refs.pull?.focus());
        },

        // The tab: a tap floats the player out, and so does dragging it up 36px (the bar follows a little).
        pullStart(e) {
            const el = e.currentTarget;
            pull = { y: e.clientY, moved: false };
            el.setPointerCapture?.(e.pointerId);
            const move = (ev) => {
                const dy = ev.clientY - pull.y;
                if (Math.abs(dy) > 4) pull.moved = true;
                this.$root.style.transform = `translateY(${Math.max(-24, Math.min(0, dy / 3))}px)`;
                if (dy < -36) { end(); this.floatOut(); }
            };
            const end = () => {
                this.$root.style.transform = '';
                el.removeEventListener('pointermove', move);
                el.removeEventListener('pointerup', end);
                el.removeEventListener('pointercancel', end);
            };
            el.addEventListener('pointermove', move);
            el.addEventListener('pointerup', end);
            el.addEventListener('pointercancel', end);
        },
        pullTap() {
            if (pull?.moved) { pull = null; return; }
            pull = null;
            this.floatOut();
        },

        /** The scrubber along the bar's bottom edge: press or drag to seek. */
        scrub(e) {
            const el = e.currentTarget;
            const at = (ev) => { const r = el.getBoundingClientRect(); this.player.seek((ev.clientX - r.left) / r.width); };
            el.setPointerCapture?.(e.pointerId);
            at(e);
            const end = () => { el.removeEventListener('pointermove', at); el.removeEventListener('pointerup', end); el.removeEventListener('pointercancel', end); };
            el.addEventListener('pointermove', at);
            el.addEventListener('pointerup', end);
            el.addEventListener('pointercancel', end);
        },

        /** Dragging the floating island down more than 40px puts it back in the bar (phones, this sound, folded island). */
        dragIsland() {
            const isl = document.querySelector('.ui-dock .ui-isl');
            if (!isl || isl.dataset.spDrag) return;
            isl.dataset.spDrag = '1';
            let start = null;
            let moved = false;
            isl.addEventListener('pointerdown', (e) => {
                moved = false;
                if (this.state !== 'float' || this.player.expanded || e.target.closest('.ui-isl-x') || !matchMedia(PHONE).matches) return;
                start = { y: e.clientY, id: e.pointerId };
            });
            isl.addEventListener('pointermove', (e) => {
                if (!start) return;
                const dy = Math.max(0, e.clientY - start.y);
                if (!moved && dy > 6) { moved = true; isl.setPointerCapture?.(start.id); }
                if (!moved) return;
                isl.style.transform = `translateY(${dy}px)`;
                isl.style.opacity = String(1 - Math.min(dy, 80) / 160);
            });
            const end = (e) => {
                if (!start) return;
                const dy = e.clientY - start.y;
                start = null;
                isl.style.transform = '';
                isl.style.opacity = '';
                if (moved && dy > 40) this.dock();
            };
            isl.addEventListener('pointerup', end);
            isl.addEventListener('pointercancel', end);
            // A drag is not a press: the folded island would open.
            isl.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
        },
    };
}
